import React, { useState, useEffect } from 'react';
import { UploadCloud, Bus, PanelLeft, Search, Bell, Sun, Monitor, Moon, HelpCircle, Edit2, X, LayoutGrid, LogOut, Info, Settings, FileText, Globe, Lock, BookOpen, QrCode } from 'lucide-react';
import '../Admin.css';
import { ParentEnrollmentModal } from './ParentEnrollmentModal';

export default function ParentPortal({ user, onLogout, BACKEND_URL }) {
  const [children, setChildren] = useState([]);
  
  // Database fields
  const [profileData, setProfileData] = useState({});
  const [avatarUrl, setAvatarUrl] = useState('');
  
  // Edit states
  const [isEditingParent, setIsEditingParent] = useState(false);
  const [editEmergencyContact, setEditEmergencyContact] = useState('');
  const [editSpouseName, setEditSpouseName] = useState('');
  const [editAvatarUrl, setEditAvatarUrl] = useState('');
  
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  
  const setActiveTab = (tab) => alert('Navigation to ' + tab + ' is disabled in Parent Portal');
  const setShowHelpCenterModal = () => alert('Help Center opened');
  const setShowLanguageModal = () => alert('Language preferences opened');
  const setShowPasswordModal = () => alert('Password reset opened');
  const setShowGuideModal = () => alert('User Guide opened');
const [parentActiveTab, setParentActiveTab] = useState('wards');
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem('themeMode') || 'light');
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const searchInputRef = React.useRef(null);
  const searchContainerRef = React.useRef(null);
  const notifMenuRef = React.useRef(null);
  const userMenuRef = React.useRef(null);
  
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
    const applyTheme = () => {
      let activeTheme = themeMode;
      if (themeMode === 'system') {
        activeTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      if (activeTheme === 'dark') {
        document.documentElement.classList.add('dark-theme');
      } else {
        document.documentElement.classList.remove('dark-theme');
      }
    };
    applyTheme();
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const listener = () => applyTheme();
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }
  }, [themeMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) setIsSearchOpen(false);
      if (notifMenuRef.current && !notifMenuRef.current.contains(event.target)) setShowNotificationsDropdown(false);
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setIsUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  useEffect(() => {
    try {
      let pd = {};
      if (user.profile_data) {
        pd = JSON.parse(user.profile_data);
      }
      setProfileData(pd);
      setAvatarUrl(pd.avatar || pd.avatarUrl || user.avatar || user.profile_image_url || '');
      setEditEmergencyContact(pd.emergencyContact || '');
      setEditSpouseName(pd.spouseName || '');
      setEditAvatarUrl(pd.avatar || pd.avatarUrl || user.avatar || user.profile_image_url || '');
    } catch (e) {}
    
    fetch(`${BACKEND_URL}/api/students/parent/${user.id}`)
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setChildren(data); })
      .catch(err => console.error(err));
  }, [user.id, user.profile_data, BACKEND_URL]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleSaveParentProfile = async () => {
    setIsSaving(true);
    try {
      const updatedProfileData = JSON.stringify({ 
        ...profileData, 
        emergencyContact: editEmergencyContact, 
        spouseName: editSpouseName,
        avatarUrl: editAvatarUrl
      });
      const res = await fetch(`${BACKEND_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...user, profile_data: updatedProfileData })
      });
      if (res.ok) {
        setAvatarUrl(editAvatarUrl);
        setProfileData(prev => ({ ...prev, emergencyContact: editEmergencyContact, spouseName: editSpouseName, avatarUrl: editAvatarUrl }));
        showToast('Profile updated successfully!');
        setIsEditingParent(false);
      }
    } catch (e) {
      console.error(e);
      showToast('Failed to update profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDPUpload = () => {
    const url = prompt("Enter Image URL for Display Picture:");
    if (url) {
      setEditAvatarUrl(url);
      showToast('DP URL updated. Please save changes to apply.');
    }
  };

  const initials = user.display_name.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase();

  return (
    <div className="admin-container font-sans">
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 bg-white p-3 rounded-lg shadow-lg border-l-4 border-emerald-500 flex items-center gap-2">
          <span className="text-emerald-500 font-bold">✓</span>
          <span className="text-sm font-semibold text-slate-800">{toastMessage}</span>
        </div>
      )}

      {/* Sidebar matching Admin login exactly */}
      <aside className="sidebar" style={{ width: "360px", minWidth: "360px", padding: "32px 32px" }}>
        <div className="sidebar-brand">
          <div className="sidebar-header flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="logo-icon w-[42px] h-[42px] bg-[#CFF53E] rounded-xl flex items-center justify-center">
                <Bus size={24} color="#131417" />
              </div>
              <span className="brand-name text-[26px] font-extrabold text-white tracking-tight">Bus Buddy</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav overflow-y-auto pr-1 pb-10">
          {/* Parent Profile Content replacing Admin Menus */}
          <div className="flex flex-col mt-4 relative pr-4">
            {!isEditingParent && (
              <button 
                onClick={() => setIsEditingParent(true)} 
                className="absolute top-4 right-4 p-2 bg-transparent text-slate-400 hover:text-white rounded-full transition-colors cursor-pointer"
                title="Edit Profile"
              >
                <Edit2 size={16} />
              </button>
            )}

            <div className="flex flex-col items-center text-center pb-6">
              <div className="relative mb-4">
                <div className="w-[100px] h-[100px] rounded-full overflow-hidden bg-slate-800/50 border border-slate-700 flex items-center justify-center text-slate-300 font-medium text-4xl tracking-tighter">
                  {avatarUrl || (isEditingParent && editAvatarUrl) ? (
                    <img src={isEditingParent ? editAvatarUrl : avatarUrl} alt="dp" className="w-full h-full object-cover" />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
                {isEditingParent && (
                  <button onClick={handleDPUpload} className="absolute bottom-0 right-0 bg-brand-600 text-white rounded-full p-1.5 shadow-md border-2 border-white hover:bg-brand-500 transition-colors">
                    <UploadCloud size={14} />
                  </button>
                )}
              </div>
              
              <h2 className="text-[20px] font-black text-white tracking-tight leading-tight">{user.display_name}</h2>
              <p className="text-[13px] text-slate-400 font-bold mt-1">{profileData.occupation || 'Principal Software Architect'}</p>
              
              <div className="flex items-center justify-center gap-2 mt-4">
                <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-slate-800 border border-slate-700 text-slate-300">ID: PRT-{String(user.id).padStart(3, '0')}</span>
                <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-amber-900/40 border border-amber-900/50 text-amber-400">{profileData.relationship || 'Father'}</span>
              </div>
            </div>
            
            <div className="w-full h-px bg-slate-800 mb-6"></div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-[12px] font-extrabold uppercase tracking-wide text-slate-200 mb-2"><span>📋</span> FAST FACTS</div>
              
              <div className="flex flex-col gap-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 font-bold text-[13px]">Primary Phone:</span>
                  <span className="font-bold text-slate-200 text-[13px]">{user.phone || '+91 9074770963'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 font-bold text-[13px]">Email Address:</span>
                  <span className="font-bold text-slate-200 text-[13px] truncate max-w-[150px]">{user.email || 'suresh.sharma@gmail.com'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 font-bold text-[13px]">Language:</span>
                  <span className="font-bold text-slate-200 text-[13px]">{profileData.language || 'English, Hindi'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 font-bold text-[13px]">Linked Wards:</span>
                  <span className="font-bold text-slate-200 text-[13px]">{children.length} student(s)</span>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-slate-800 my-6"></div>

            <div className="space-y-4">
              <div className="flex items-center gap-2 text-[12px] font-extrabold uppercase tracking-wide text-slate-200 mb-2"><span>🚨</span> EMERGENCY & SPOUSE</div>
              
              <div className="flex flex-col gap-4 text-[13px]">
                <div>
                  <div className="text-slate-400 font-bold mb-1">Spouse / Secondary Guardian:</div>
                  {isEditingParent ? (
                    <input 
                      type="text" 
                      value={editSpouseName} 
                      onChange={(e) => setEditSpouseName(e.target.value)}
                      placeholder="e.g. Dr. Ritu Sharma"
                      className="w-full p-2 border border-slate-300 rounded-lg text-[13px] font-bold text-slate-200 outline-none focus:border-brand-500"
                    />
                  ) : (
                    <div className="font-bold text-slate-200">{profileData.spouseName || 'Dr. Ritu Sharma (Pediatrician)'}</div>
                  )}
                </div>
                <div>
                  <div className="text-slate-400 font-bold mb-1">Emergency Contact:</div>
                  {isEditingParent ? (
                    <input 
                      type="text" 
                      value={editEmergencyContact} 
                      onChange={(e) => setEditEmergencyContact(e.target.value)}
                      placeholder="e.g. +91 98860 55432"
                      className="w-full p-2 border border-slate-300 rounded-lg text-[13px] font-bold text-slate-200 outline-none focus:border-brand-500"
                    />
                  ) : (
                    <div className="font-bold text-rose-400">{profileData.emergencyContact || '+91 98860 55432 (Dr. Ritu Sharma)'}</div>
                  )}
                </div>
                <div>
                  <div className="text-slate-400 font-bold mb-1">Pickup Authorization:</div>
                  <div className="font-bold text-emerald-400 flex items-center gap-1">
                    <span>✓</span> {profileData.pickupAuth || 'Authorized for self & spouse'}
                  </div>
                </div>
              </div>

              {isEditingParent && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                  <button 
                    onClick={() => {
                      setIsEditingParent(false);
                      setEditAvatarUrl(avatarUrl);
                      setEditSpouseName(profileData.spouseName || '');
                      setEditEmergencyContact(profileData.emergencyContact || '');
                    }} 
                    className="flex-1 py-2.5 bg-[#1E293B] hover:bg-[#334155] text-slate-500 hover:text-slate-300 text-[13px] font-bold rounded-[10px] transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSaveParentProfile} 
                    disabled={isSaving} 
                    className="flex-1 py-2 bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold rounded-xl transition-colors shadow-sm disabled:opacity-70"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      </aside>

            {/* Content Area */}
      <div className="content-area">
        {/* Admin Header perfectly replicated */}
        <header className="content-header-modern">
          <div className="header-left-section">
            <div className="header-search-container" style={{ display: "none" }} ref={searchContainerRef}>
              <div className="header-search-bar" onClick={() => { setIsSearchOpen(true); searchInputRef.current?.focus(); }}>
                <Search size={15} color="#64748B" className="header-search-icon" />
                <input 
                  ref={searchInputRef}
                  type="text" 
                  placeholder="Search menus or entities..." 
                  value={headerSearchQuery}
                  onFocus={() => setIsSearchOpen(true)}
                  onChange={(e) => {
                    setHeaderSearchQuery(e.target.value);
                    setIsSearchOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsSearchOpen(false);
                    }
                  }}
                  className="header-search-input" 
                />
                {headerSearchQuery ? (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setHeaderSearchQuery(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '0 2px', display: 'flex', alignItems: 'center' }}
                  >
                    <X size={14} />
                  </button>
                ) : (
                  <kbd className="header-search-kbd">⌘K</kbd>
                )}
              </div>

              {/* SEARCH RESULTS DROPDOWN */}
              {isSearchOpen && headerSearchQuery.trim().length > 0 && (
                <div className="search-results-dropdown" style={{ animation: 'dropdownFade 0.15s ease-out' }}>
                  {true ? (
                    <div className="search-empty-state">
                      <Search size={22} color="#94A3B8" />
                      <div style={{ fontWeight: 600, color: '#475569', fontSize: '13px', marginTop: '6px' }}>No matches found</div>
                      <div style={{ fontSize: '12px', color: '#94A3B8' }}>No menu items or entities matching "{headerSearchQuery}"</div>
                    </div>
                  ) : (
                    <div className="search-results-scrollable">
                      {/* MENU ITEMS */}
                      {searchResults.menus.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Navigation & Menu ({searchResults.menus.length})</div>
                          {searchResults.menus.map(item => {
                            const ItemIcon = item.icon || LayoutGrid;
                            return (
                              <div 
                                key={item.id} 
                                className="search-result-item"
                                onClick={() => {
                                  if (item.action) {
                                    item.action();
                                  } else {
                                    setActiveTab(item.id);
                                    if (item.id === 'trips' || item.id === 'reports' || item.id === 'students' || item.id === 'parents' || item.id === 'drivers' || item.id === 'buses' || item.id === 'documents' || item.id === 'access_control') {
                                      fetchManagementData();
                                    }
                                  }
                                  setIsSearchOpen(false);
                                  setHeaderSearchQuery('');
                                }}
                              >
                                <div className="search-result-left">
                                  <div className="search-icon-badge menu-badge">
                                    <ItemIcon size={15} />
                                  </div>
                                  <div>
                                    <div className="search-result-title">{item.title}</div>
                                    <div className="search-result-sub">{item.desc}</div>
                                  </div>
                                </div>
                                <span className="search-tag-pill menu-tag">{item.category}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* STUDENTS */}
                      {searchResults.students.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Students ({searchResults.students.length})</div>
                          {searchResults.students.map(student => (
                            <div 
                              key={student.id} 
                              className="search-result-item"
                              onClick={() => {
                                handleStudentClick(student);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge student-badge">
                                  <Users size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{student.first_name} {student.last_name}</div>
                                  <div className="search-result-sub">
                                    {student.grade ? `${student.grade} • ` : ''}RFID: {student.rfid_tag_id || 'N/A'} • Stop: {student.pickup_stop_name || 'Assigned'}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill student-tag">Student</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* PARENTS */}
                      {searchResults.parents && searchResults.parents.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Parents & Guardians ({searchResults.parents.length})</div>
                          {searchResults.parents.map(parent => (
                            <div 
                              key={parent.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('parents');
                                handleParentClick(parent);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge parent-badge" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}>
                                  <User size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{parent.display_name}</div>
                                  <div className="search-result-sub">
                                    Phone: {parent.phone || 'N/A'} • {allStudents.filter(s => s.parent_id === parent.id).map(s => s.first_name).join(', ') ? `Wards: ${allStudents.filter(s => s.parent_id === parent.id).map(s => s.first_name).join(', ')}` : 'Parent'}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}>Parent</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* DRIVERS */}
                      {searchResults.drivers.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Drivers ({searchResults.drivers.length})</div>
                          {searchResults.drivers.map(driver => (
                            <div 
                              key={driver.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('drivers');
                                handleDriverClick(driver);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge driver-badge">
                                  <Shield size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{driver.display_name || driver.username}</div>
                                  <div className="search-result-sub">
                                    Phone: {driver.phone || 'N/A'} • {driver.email || 'Driver Staff'}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill driver-tag">Driver</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* BUSES */}
                      {searchResults.buses.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">School Buses ({searchResults.buses.length})</div>
                          {searchResults.buses.map(bus => (
                            <div 
                              key={bus.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('buses');
                                handleBusClick(bus);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge bus-badge">
                                  <Bus size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">Bus #{bus.bus_number}</div>
                                  <div className="search-result-sub">
                                    Reg: {bus.registration_no || 'N/A'} • Capacity: {bus.capacity || 40} Seats
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill bus-tag">Vehicle</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ROUTES */}
                      {searchResults.routes.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Routes ({searchResults.routes.length})</div>
                          {searchResults.routes.map(route => (
                            <div 
                              key={route.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('monitor');
                                setFilterRouteId(String(route.id));
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge route-badge">
                                  <MapPin size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{route.name}</div>
                                  <div className="search-result-sub">
                                    {route.description || 'Assigned Transit Route'}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill route-tag">Route</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* TRIPS */}
                      {searchResults.trips.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Active Trips ({searchResults.trips.length})</div>
                          {searchResults.trips.map(trip => (
                            <div 
                              key={trip.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('monitor');
                                setSelectedTrip(trip);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge trip-badge">
                                  <Clock size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{trip.route_name || `Trip #${trip.id}`} (Bus #{trip.bus_number})</div>
                                  <div className="search-result-sub">
                                    Driver: {trip.driver_name || 'Assigned'} • Status: {trip.status}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill trip-tag">Trip</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="header-right-section">
            {/* Notification Bell with Badge 6 */}
            <div className="notif-wrapper" ref={notifMenuRef}>
              <button 
                className="header-icon-btn notif-bell-btn"
                onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
                title="Notifications"
              >
                <Bell size={18} color="#EF4444" />
                <span className="notif-badge-pill">6</span>
              </button>
              {showNotificationsDropdown && (
                <div className="notif-dropdown-menu">
                  <div className="notif-dropdown-header">
                    <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>System Notifications (6)</span>
                    <span 
                      style={{ fontSize: '11px', color: '#3B82F6', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => { showToast('All notifications marked as read'); setShowNotificationsDropdown(false); }}
                    >
                      Mark all read
                    </span>
                  </div>
                  <div className="notif-dropdown-list">
                    <div className="notif-dropdown-item" onClick={() => { setActiveTab('reports'); setShowNotificationsDropdown(false); }}>
                      <div className="notif-item-dot" style={{ backgroundColor: '#EF4444' }}></div>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>SOS Alert: Bus #102</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Driver reported heavy roadblock on North Route</div>
                        <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>2 mins ago</div>
                      </div>
                    </div>
                    <div className="notif-dropdown-item" onClick={() => { setActiveTab('monitor'); setShowNotificationsDropdown(false); }}>
                      <div className="notif-item-dot" style={{ backgroundColor: '#F59E0B' }}></div>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Speed Warning: Route #4</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Speed exceeded 45 km/h on Ring Road corridor</div>
                        <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>14 mins ago</div>
                      </div>
                    </div>
                    <div className="notif-dropdown-item" onClick={() => { setActiveTab('trips'); setShowNotificationsDropdown(false); }}>
                      <div className="notif-item-dot" style={{ backgroundColor: '#10B981' }}></div>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Morning Trips Completed</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>14 of 14 morning routes finished with 100% check-in</div>
                        <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>35 mins ago</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Theme Toggle Group (Sun, Monitor, Moon) */}
            <div className="theme-toggle-group">
              <button 
                className={`theme-toggle-btn ${themeMode === 'light' ? 'active-sun' : ''}`}
                onClick={() => setThemeMode('light')}
                title="Light Mode"
              >
                <Sun size={15} color={themeMode === 'light' ? '#F59E0B' : '#94A3B8'} />
              </button>
              <button 
                className={`theme-toggle-btn ${themeMode === 'system' ? 'active' : ''}`}
                onClick={() => setThemeMode('system')}
                title="System Mode"
              >
                <Monitor size={15} color={themeMode === 'system' ? '#3B82F6' : '#94A3B8'} />
              </button>
              <button 
                className={`theme-toggle-btn ${themeMode === 'dark' ? 'active' : ''}`}
                onClick={() => setThemeMode('dark')}
                title="Dark Mode"
              >
                <Moon size={15} color={themeMode === 'dark' ? '#6366F1' : '#94A3B8'} />
              </button>
            </div>

            {/* Help Question Button */}
            <button 
              className="header-icon-btn help-btn"
              onClick={() => setShowHelpCenterModal(true)}
              title="Help & Documentation"
            >
              <HelpCircle size={18} color="#8B5CF6" />
            </button>

            {/* User DP & Context Menu */}
            <div className="header-user-menu-container" ref={userMenuRef}>
              <div 
                className="header-user-btn" 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                title="Account Menu"
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: '1.2' }}>
                  <span className="header-username">{user?.display_name || user?.username || 'admin'}</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {user?.role === 'admin' ? 'System Admin' : user?.role === 'route_manager' ? 'Route Manager' : (user?.role === 'viewer_guard' || user?.role === 'guard' || user?.role === 'viewer') ? 'Viewer / Guard' : (user?.role || 'Admin')}
                  </span>
                </div>
                <div className="header-user-dp" style={{ width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={user?.display_name || 'avatar'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{fontSize: '12px', fontWeight: 'bold', color: '#475569'}}>{initials}</span>
                    )}
                  </div>
              </div>

              {/* Context Menu Dropdown */}
              {isUserMenuOpen && (
                <div className="user-context-menu">
                    <button 
                      className="context-menu-item"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setShowPasswordModal();
                      }}
                    >
                      <Lock size={16} className="context-menu-icon" />
                      <span>Password</span>
                    </button>
                    <button 
                      className="context-menu-item"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setShowEnrollmentModal(true);
                      }}
                    >
                      <QrCode size={16} className="context-menu-icon" />
                      <span>Mobile App Activation</span>
                    </button>
                    <button 
                      className="context-menu-item logout"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      onLogout();
                    }}
                  >
                    <LogOut size={16} className="context-menu-icon" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Body containing Parent Tab Content */}
        <div className="page-body-container flex-1 overflow-y-auto bg-slate-50 p-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Parent & Guardian Profile</h1>
              <p className="text-xs text-slate-500 font-medium">Real-time status, ward tracking, and communications hub</p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
              <button onClick={() => setParentActiveTab('wards')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center gap-1.5 transition ${parentActiveTab === 'wards' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>👥 Linked Wards ({children.length})</button>
              <button onClick={() => setParentActiveTab('address')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center gap-1.5 transition ${parentActiveTab === 'address' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>📍 Address & Route Info</button>
              <button onClick={() => setParentActiveTab('alerts')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center gap-1.5 transition ${parentActiveTab === 'alerts' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>🔔 Alerts & Notifications</button>
              <button onClick={() => setParentActiveTab('activity')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center gap-1.5 transition ${parentActiveTab === 'activity' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>💬 Activity & Support</button>
            </div>
          </div>

          {parentActiveTab === 'wards' && (
            <section className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 space-y-4 text-slate-800">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-base">🧑‍🤝‍🧑</span>
                  <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">Linked Students / Wards</h2>
                  <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-brand-50 text-brand-600 rounded-full border border-brand-200">{children.length} Enrolled</span>
                </div>
                <span className="text-xs font-semibold text-slate-400">Academic Year 2024-25</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {children.map(child => (
                  <div key={child.id} className="border border-slate-200 rounded-xl p-4 bg-white hover:border-brand-300 transition-all shadow-sm flex flex-col justify-between space-y-4">
                    <div>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-700 font-extrabold flex items-center justify-center text-sm border border-slate-200 shadow-inner relative">
                            <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center">
                              {child.avatar ? (
                                <img src={child.avatar} alt={child.first_name} className="w-full h-full object-cover" />
                              ) : (
                                <>{child.first_name[0]}{child.last_name[0]}</>
                              )}
                            </div>
                            {isEditingParent && (
                              <button onClick={handleDPUpload} className="absolute -bottom-1 -right-1 bg-brand-600 text-white rounded-full p-1 shadow-sm border border-white hover:bg-brand-500 transition-colors" title="Upload Ward DP">
                                <UploadCloud size={10} />
                              </button>
                            )}
                          </div>
                          <div>
                            <h3 className="font-extrabold text-slate-900 text-base leading-snug">{child.first_name} {child.last_name}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] font-bold bg-brand-50 text-brand-700 px-2 py-0.5 rounded border border-brand-200">ADM-{child.id}</span>
                              <span className="text-xs font-semibold text-slate-400">Class {child.grade || 'N/A'} {child.section ? `(Section ${child.section})` : ''}</span>
                            </div>
                          </div>
                        </div>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          In School
                        </span>
                      </div>
                      <div className="mt-4 pt-3 border-t border-slate-100 space-y-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400 font-medium">RFID Tag:</span>
                          <span className="font-mono font-bold text-emerald-600 bg-emerald-50/50 px-2 py-0.5 rounded">{child.rfid_tag_id || 'Not Assigned'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400 font-medium">Bus Route:</span>
                          <span className="font-bold text-slate-700">Bus #{child.route_id || 'N/A'}</span>
                        </div>
                        <div className="flex items-start gap-2 pt-1 text-[11px] text-slate-500">
                          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span><span className="font-medium text-slate-700">Pickup:</span> {child.pickup_stop_name || 'Not Set'}</div>
                          <span className="text-slate-300">•</span>
                          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span><span className="font-medium text-slate-700">Drop:</span> {child.drop_stop_name || 'Not Set'}</div>
                        </div>
                      </div>
                    </div>
                    {isEditingParent ? (
                      <button onClick={handleDPUpload} className="w-full py-2 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg text-xs font-bold text-indigo-700 flex items-center justify-center gap-2 shadow-sm transition-colors">
                        <UploadCloud size={14} />
                        Upload Ward Documents
                      </button>
                    ) : (
                      <button className="w-full py-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg text-xs font-bold text-slate-700 flex items-center justify-center gap-2 shadow-sm transition-colors">
                        👤 View Student Profile
                      </button>
                    )}
                  </div>
                ))}
                {children.length === 0 && <div className="text-xs text-slate-400">No students linked to this account.</div>}
              </div>

              {children.length > 0 && (
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200/70 flex flex-col md:flex-row md:items-center justify-between text-xs text-slate-600 gap-2 mt-4">
                  <div className="flex items-center gap-2 font-medium">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                    <span className="font-bold text-slate-800">Today's Transit Status:</span>
                    <span>Bus #{children[0]?.route_id || 'N/A'} safely reached campus at 8:20 AM</span>
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500 flex items-center gap-3">
                    {children.map(child => (
                      <span key={child.id}>• {child.first_name}: Checked In</span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {parentActiveTab !== 'wards' && (
            <section className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 space-y-4 text-slate-800">
               <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                 <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">
                   {parentActiveTab === 'address' && 'Address & Route Info'}
                   {parentActiveTab === 'alerts' && 'Alerts & Notifications'}
                   {parentActiveTab === 'activity' && 'Activity & Support'}
                 </h2>
               </div>
               <div className="p-4 text-sm text-slate-500">
                  {parentActiveTab === 'address' && 'No additional address info available.'}
                  {parentActiveTab === 'alerts' && 'No new alerts or notifications.'}
                  {parentActiveTab === 'activity' && 'No recent activity.'}
               </div>
            </section>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-slate-800">
            <section className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-base">🔔</span>
                  <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">Alerts & Notifications</h2>
                </div>
                <span className="px-2 py-0.5 text-xs font-bold bg-rose-50 text-rose-600 rounded-full border border-rose-200">1 New</span>
              </div>
              <div className="space-y-3">
                <div className="p-3 bg-brand-50/50 border border-brand-100 rounded-xl flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-900">Bus Update</span>
                      <span className="text-[10px] text-slate-400 font-medium">8:12 AM</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">Bus is currently on schedule and has arrived at campus.</p>
                  </div>
                </div>
              </div>
            </section>
            
            <section className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 space-y-4 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-base">💬</span>
                    <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">Support & Direct Messages</h2>
                  </div>
                  <span className="text-xs font-bold text-slate-500 hover:text-brand-600 cursor-pointer">View All</span>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="p-3.5 rounded-xl border border-slate-100 bg-slate-50 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center">TD</div>
                        <div>
                          <div className="text-xs font-bold text-slate-800">Transport Dispatch Desk</div>
                          <div className="text-[10px] text-slate-400">Operations</div>
                        </div>
                      </div>
                      <span className="text-[10px] text-slate-400 font-medium">Yesterday</span>
                    </div>
                    <p className="text-xs text-slate-600">Confirmed: Drop point verified for the upcoming term.</p>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-100 grid grid-cols-3 gap-2">
                <button className="py-2 px-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold text-center transition-colors truncate">✉️ Message Teacher</button>
                <button className="py-2 px-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold text-center transition-colors truncate">🎫 Raise Ticket</button>
                <button className="py-2 px-2 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-xs font-bold text-center transition-colors border border-rose-200 truncate">🆘 Emergency SOS</button>
              </div>
            </section>
          </div>
        </div>
      
      {showEnrollmentModal && (
        <ParentEnrollmentModal
          parentId={user.id}
          parentName={user.display_name || user.username}
          backendUrl={BACKEND_URL}
          onClose={() => setShowEnrollmentModal(false)}
        />
      )}
</div>
    </div>
  );
}
